// @ts-nocheck
import 'regenerator-runtime/runtime'
import * as uuid from 'uuid'
import logger from './common/logger'
import {initPersistentIdentifier} from './persistentIdentifier'
import EventDataService from './services/eventDataService'
import PixelConfigurationService from './services/pixelConfigurationService'
import everflowTracker from './third-party-trackers/everflow'
import fbTracker from './third-party-trackers/facebook'
import guTracker from './third-party-trackers/giddyupServerSide'
import googleAdsTracker from './third-party-trackers/googleAds'
import gtagTracker from './third-party-trackers/gtagUniversalAnalytics'
import redshiftTracker from './third-party-trackers/redshift'
import snapchatTracker from './third-party-trackers/snapchat'
import tiktokTracker from './third-party-trackers/tiktok'
import VwoTracker from './third-party-trackers/vwo'
import {getExclusiveDomainParts, getUrlParams} from './utils/urls'
import GA4 from './third-party-trackers/ga4';
import AwinTracker from './third-party-trackers/awin';
import AppLovinTracker from './third-party-trackers/appLovin';

window.gu_qs = window.gu_qs || {}
try {
  initPersistentIdentifier()
} catch (e) {
  //console.log(e);
}


fbTracker.load();

// Function to fetch the IP address
const fetchIpAddress = async () => {
  if (window.cachedIpAddress) {
    return window.cachedIpAddress;
  }

  try {
    const response = await fetch('https://api64.ipify.org?format=json');
    const data = await response.json();
    window.cachedIpAddress = data.ip;
    return window.cachedIpAddress;
  } catch (error) {
    console.log('Error fetching IP address:', error);
    return null;
  }
};

const module = 'TrackingManager'
const TrackingManager = {
  initRetryCount: 0,
  isInitialized: false,
  eventQueue: [],
  cookieCheckInterval: null,
  vwoCookieCheckAttempts: 0,
  config: {},
  initTracking: async (config, eventQueue) => {
    try {
      TrackingManager.eventQueue = eventQueue
      TrackingManager.setConfig(config)
      TrackingManager.initCorrelationId()
      EventDataService.populateOfferGuParams() // populates window.gu_qs if not already available.

      await fetchIpAddress()
      if(window.gu_qs && (!window.gu_qs['req_id'] || window.gu_qs['req_id'] === '' || window.gu_qs['req_id'] === 'undefined')) {
        // If VWO is enabled, we need to wait for the VWO cookies to land before tracking the click
        if (TrackingManager.config?.vwo_enabled) {
          TrackingManager.cookieCheckInterval = setInterval(async () => {
            if (VwoTracker.checkVwoCookie() || TrackingManager.vwoCookieCheckAttempts > 10) {
              clearInterval(TrackingManager.cookieCheckInterval)
              await everflowTracker.trackClick()
              TrackingManager.finishInit()
            }
            TrackingManager.vwoCookieCheckAttempts++
          }, 100)
        }
        else {
          await everflowTracker.trackClick()
          TrackingManager.finishInit()
        }
      } else {
        // We already have a req_id, so we can finish init
        TrackingManager.finishInit()
      }
    } catch (e) {
      logger.error('initTracking Failed', e, {module})
    }
  },
  finishInit: async () => {
    // Prevent multiple calls to finishInit
    if (TrackingManager.isInitialized) {
      return;
    }

    logger.addGlobalContext(window?.gu_qs)

    const consentModeEnabled = TrackingManager.config.consent_mode === true

    if (consentModeEnabled) {
      // Load Google services in consent mode
      gtagTracker.load(TrackingManager.config.ga_account_id, true, consentModeEnabled)
      GA4.load(TrackingManager.config.gu_ga4_id, true, consentModeEnabled)
    }
    else {
      await TrackingManager.initExtendedTracking(false)
    }

    AwinTracker.load()

    TrackingManager.isInitialized = true
    TrackingManager.clearEventQ()
    TrackingManager.fireEvent({eventName: 'Page Start'})
  },
  initExtendedTracking: async (deferred = true) => {
    try {
      if (deferred) {
        gtagTracker.updateConsent()
        GA4.updateConsent()
      }
      else {
        gtagTracker.load(TrackingManager.config.ga_account_id)
        GA4.load(TrackingManager.config.gu_ga4_id)
      }
      VwoTracker.load(
        TrackingManager.config?.vwo_enabled,
        TrackingManager.config?.vwo_page_type
      )
      await TrackingManager.setupPixelsAsync()
      AwinTracker.grantConsent()
    } catch (e) {
      logger.error('initExtendedTracking Failed', e, {module})
    }
  },
  setConfig(config){
    TrackingManager.config = config || {};
  },
  clearEventQ: () => {
    try {
      if (
        TrackingManager?.eventQueue &&
        TrackingManager?.eventQueue.length > 0
      ) {
        for (let event of TrackingManager?.eventQueue) {
          TrackingManager.fireEvent(event)
        }
        TrackingManager.eventQueue = []
      }
    } catch (e) {
      logger.error('clearEventQ Failed', e, {module})
    }
  },
  initCorrelationId: () => {
    window.gu_correlationId = uuid.v4()
  },
  setupPixelsAsync: async () => {
    try {
      TrackingManager.PixelConfiguration =
        await PixelConfigurationService.getPixelConfigurationForCurrentPageAsync()
      logger.info('Pixel Configuration', {
        pixelConfig: TrackingManager.PixelConfiguration,
      })

      fbTracker.initAdditional(TrackingManager?.PixelConfiguration?.facebook)
      snapchatTracker.load(TrackingManager?.PixelConfiguration?.snapchat)
      tiktokTracker.load(TrackingManager?.PixelConfiguration?.tiktok)
      googleAdsTracker.load(TrackingManager?.PixelConfiguration?.google)
      AppLovinTracker.load(TrackingManager?.PixelConfiguration?.applovin)
    } catch (e) {
      logger.error('setupPixelOverrides Failed', e, {module})
    }
  },
  fireEvent: (props) => {
    try {
      if(typeof props === 'string'){
        props = {
          eventName: props
        };
      }
      let {
        eventName,
        action,
        identifier,
        propertyName,
        propertyValue,
        pagePath,
        propertyMeta
      } = props;
      if (!TrackingManager.isInitialized) {
        const queuedEvent = {
          eventName,
          action,
          identifier,
          propertyName,
          propertyValue,
          pagePath,
          propertyMeta
        }
        TrackingManager.eventQueue.push(queuedEvent)
      } else {
        let eventProperties

        if(
          window?.gu_qs?.gutestpurchase === '1' &&
          eventName === 'Landing Page'
        ){
          eventName = 'Order Conversion'
          eventProperties = EventDataService.buildTestEventProperties()
        }else{
          eventProperties = EventDataService.buildEventProperties(
            eventName,
            action,
            identifier,
            propertyName,
            propertyValue,
            pagePath,
            propertyMeta
          )
        }

        if (eventName === 'Page View') {
          TrackingManager.fireMatchingPageViewEvent()
          return
        }
        if (eventName === 'Page Start') {
          TrackingManager.fireMatchingPageStartEvent()
          return
        }

        if ([
          'Presell Page',
          'Landing Page',
          'Custom Landing Page',
          'Landing Page Start',
          'Splash Page',
        ].includes(eventName) && (props?.adv2 || TrackingManager.config?.adv2)) {
          eventProperties.adv2 = props?.adv2 || TrackingManager.config?.adv2
        }

        if (window?.gu_qs?.gutestpurchase === '1') {
          logger.debug('Fire Test Tracking Event', {eventProperties})
        } else {
          logger.debug('Fire Tracking Event', {eventProperties})
          everflowTracker.trackEvent(eventProperties)
        }

        fbTracker.trackEvent(eventProperties)
        tiktokTracker.trackEvent(eventProperties)
        snapchatTracker.trackEvent(eventProperties)
        gtagTracker.trackEvent(eventProperties)
        GA4.trackEvent(eventProperties)
        googleAdsTracker.trackEvent(eventProperties)
        guTracker.trackEvent(eventProperties)
        VwoTracker.trackEvent(eventProperties)
        redshiftTracker.trackEvent(eventProperties)
        AwinTracker.trackEvent(eventProperties)
        AppLovinTracker.trackEvent(eventProperties)
      }
    } catch (e) {
      logger.error('fireEvent Failed', e, {module})
    }
  },
  fireMatchingPageViewEvent: () => {
    try {
      const path = window.location.pathname.toLowerCase()
      if (path.startsWith('/splash')) {
        TrackingManager.fireEvent({eventName: 'Splash Page'})
      } else if (path.startsWith('/blog')) {
        TrackingManager.fireEvent({eventName: 'Presell Page'})
      } else {
        TrackingManager.fireEvent({eventName: 'Landing Page'})
      }
    } catch (e) {
      logger.error('fireMatchingPageViewEvent Failed', e, { module })
    }
  },
  fireMatchingPageStartEvent: () => {
    try {
      const path = window.location.pathname.toLowerCase()
      if (path.startsWith('/splash')) {
        TrackingManager.fireEvent({eventName: 'Splash Page Start'})
      } else if (path.startsWith('/blog')) {
        TrackingManager.fireEvent({eventName: 'Presell Page Start'})
      } else {
        TrackingManager.fireEvent({eventName: 'Landing Page Start'})
      }
    } catch (e) {
      logger.error('fireMatchingPageStartEvent Failed', e, { module })
    }
  },
  async guInitPixelConfig(options = {}) {
    try {
      logger.debug('Invoked guInitPixelConfig()', options)
      let pixelConfig
      const {brandName, affiliateId, offerId} = options

      pixelConfig =
        await PixelConfigurationService.getPixelConfigFromBrandNameAndAffIdAndOId(
          brandName,
          affiliateId,
          offerId
        )

      logger.info('Pixel Configuration', {
        pixelConfig: TrackingManager.PixelConfiguration,
      })

      if (!pixelConfig) {
        console.log('No pixelConfigs found!', {options})
        return false
      }

      TrackingManager.PixelConfiguration = pixelConfig

      // Init the different clients
      fbTracker.initAdditional(TrackingManager?.PixelConfiguration?.facebook)
      snapchatTracker.load(TrackingManager?.PixelConfiguration?.snapchat)
      tiktokTracker.load(TrackingManager?.PixelConfiguration?.tiktok)
      googleAdsTracker.load(TrackingManager?.PixelConfiguration?.google)

      console.log('success guInitPixelConfig()')
      return true
    } catch (error) {
      console.log('failed guInitPixelConfig()', {error})
      logger.error('Error in guInitPixelConfig()', {error, options})
      return false
    }
  },
  async guInitPixelFromCustomDomain(customDomain) {
    try {
      const {brandName} = getExclusiveDomainParts(customDomain)
      const {aff_id, oid, req_id} = getUrlParams(customDomain)

      window.gu_qs = window.gu_qs || {}
      window.gu_qs['req_id'] = req_id
      window.gu_qs['externalId'] = req_id

      return await this.guInitPixelConfig({
        brandName,
        affiliateId: aff_id,
        offerId: oid,
      })
    } catch (error) {
      console.log('failed guInitPixelFromCustomDomain()', {error})
      logger.error('Error in guInitPixelFromCustomDomain()', {
        error,
        customDomain,
      })
      return false
    }
  },
}

window.gu_fire_event = TrackingManager.fireEvent
window.gu_init_pixel_config = TrackingManager.guInitPixelConfig
window.init_pixel_from_custom_domain =
  TrackingManager.guInitPixelFromCustomDomain.bind(TrackingManager)

window.gu_tracking = TrackingManager;
export default TrackingManager
